import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Container from '@mui/material/Container';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/sl';
import { useEffect, useRef, useState } from 'react';
import { ArticleApi } from '../../../network';
import { ArticleAdminModel, ArticleType } from '../../../network/rest';
import ApiErrorHandler from '../../../utils/ApiErrorHandler';
import { translations } from '../../../utils/staticContent';
import ImageUploadForm from '../../components/ImageUploadForm';
import RichTextEditor from '../../components/RichTextEditor/RichTextEditor';

export default function ArticlForm({
    article,
    articleTypes,
}: {
    article?: ArticleAdminModel;
    articleTypes: ArticleType[];
}) {
    useEffect(() => {
        if (article) {
            getData(article.id);
        }
    }, []);

    async function getData(id: string) {
        try {
            const response = await ArticleApi.getArticleAdminApiV1ArticlesArticleAdminGet({
                id,
            });
            if (response) {
                setTitle(response.title);
                setContent(response.content);
                setType(response.type);
                setAuthor(response.author);
                setHtml((response.html as string) || '');
                setCreatedAt(response.createdAt ? dayjs(response.createdAt) : null);
                setPublished(!!response.published);
                loadedPublished.current = !!response.published;
            }
        } catch (e) {
            ApiErrorHandler(e);
        }
    }

    const [title, setTitle] = useState(article?.title || '');
    const [content, setContent] = useState(article?.content || '');
    const [type, setType] = useState(article?.type || '');
    const [author, setAuthor] = useState(article?.author || '');
    const [html, setHtml] = useState('');
    const [fileId, setFileId] = useState('');
    const [createdAt, setCreatedAt] = useState<Dayjs | null>(
        article?.createdAt ? dayjs(article.createdAt) : null,
    );
    const [createdAtError, setCreatedAtError] = useState(false);
    const [published, setPublished] = useState(!!article?.published);
    // publishing an existing article goes through its own endpoints, so the
    // loaded value is kept to tell whether the checkbox was actually toggled
    const loadedPublished = useRef(!!article?.published);

    const [error, setError] = useState('');

    return (
        <div className="ArticlForm">
            <Container component="main" maxWidth="md">
                <Grid
                    container
                    component="form"
                    rowSpacing={2}
                    columnSpacing={{ xs: 1, sm: 2 }}
                    noValidate
                    mt={1}
                    onSubmit={async (e) => {
                        e.preventDefault();
                        try {
                            if (article) {
                                await ArticleApi.updateArticleApiV1ArticlesArticlePut({
                                    id: article.id,
                                    articleUpdateModel: {
                                        title,
                                        content,
                                        type,
                                        html,
                                        createdAt: createdAt
                                            ? createdAt.format('YYYY-MM-DD')
                                            : undefined,
                                    },
                                });

                                if (published !== loadedPublished.current) {
                                    if (published) {
                                        await ArticleApi.publishArticleApiV1ArticlesArticlePublishPost(
                                            { id: article.id },
                                        );
                                    } else {
                                        await ArticleApi.unpublishArticleApiV1ArticlesArticleUnpublishPost(
                                            { id: article.id },
                                        );
                                    }
                                    loadedPublished.current = published;
                                }
                            } else {
                                await ArticleApi.createArticleApiV1ArticlesArticlePost({
                                    articleCreateModel: {
                                        title,
                                        content,
                                        type: type as ArticleType,
                                        author,
                                        html,
                                        fileId,
                                        published,
                                        createdAt: createdAt
                                            ? createdAt.format('YYYY-MM-DD')
                                            : undefined,
                                    },
                                });
                                setTitle('');
                                setContent('');
                                setType('');
                                setAuthor('');
                                setHtml('');
                                setCreatedAt(null);
                                setPublished(false);
                            }
                        } catch (e) {
                            const error = ApiErrorHandler(e);
                            setError(error);
                        }
                    }}
                >
                    <Grid item xs={12} sm={12}>
                        <TextField
                            required
                            fullWidth
                            id="title"
                            label={translations.title}
                            name="title"
                            autoFocus
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            required
                            fullWidth
                            id="type"
                            value={type}
                            name="type"
                            label={translations.type}
                            select
                            onChange={(e) => setType(e.target.value)}
                        >
                            {articleTypes.map((i) => (
                                <MenuItem value={i} key={i}>
                                    {i}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            required
                            fullWidth
                            name="author"
                            label={translations.author}
                            id="author"
                            value={author}
                            onChange={(e) => setAuthor(e.target.value)}
                            disabled={!!article}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="sl">
                            <DatePicker
                                label={translations.date}
                                value={createdAt}
                                onChange={(newValue) => setCreatedAt(newValue)}
                                format="DD/MM/YYYY"
                                slotProps={{ textField: { fullWidth: true } }}
                                onError={(e) => setCreatedAtError(!!e)}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    name="published"
                                    id="published"
                                    checked={published}
                                    onChange={(e) => setPublished(e.target.checked)}
                                />
                            }
                            label={translations.publish}
                        />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <RichTextEditor
                            value={content}
                            onChange={setContent}
                            label={translations.content}
                        />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <TextField
                            fullWidth
                            name="html"
                            label="html"
                            id="html"
                            value={html}
                            onChange={(e) => setHtml(e.target.value)}
                            multiline={true}
                        />
                    </Grid>
                    <ImageUploadForm setError={setError} fileId={fileId} setFileId={setFileId} />
                    <Grid item xs={12} sm={12}>
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, mb: 2 }}
                            disabled={
                                title.length < 5 ||
                                title.length > 50 ||
                                !type.length ||
                                content.length < 5 ||
                                content.length > 5000 ||
                                author.length < 2 ||
                                author.length > 20 ||
                                createdAtError
                            }
                        >
                            {translations.save}
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <Typography component="span" color="red">
                            {error}
                        </Typography>
                    </Grid>
                </Grid>
            </Container>
        </div>
    );
}
